import { useMemo, useState } from 'react';
import type { OptionGroup } from '../types';

type MultiSelectProps = {
  label: string;
  group: OptionGroup;
  values: string[];
  onChange: (next: string[]) => void;
  hint?: string | undefined;
};

// Multi-select rendered as chip toggles. "Other" reveals a small inline input;
// pressing Enter (or clicking +) appends the typed value as a custom chip.
// Custom chips render with a different accent so users can see what came from
// the canonical vocabulary vs. their own free text.
export function MultiSelect({ label, group, values, onChange, hint }: MultiSelectProps) {
  const knownValues = useMemo(() => new Set(group.options.map((o) => o.value)), [group.options]);
  const [customInput, setCustomInput] = useState('');
  const [customOpen, setCustomOpen] = useState(false);

  const customChips = values.filter((v) => !knownValues.has(v));

  const toggle = (v: string): void => {
    onChange(values.includes(v) ? values.filter((x) => x !== v) : [...values, v]);
  };

  const addCustom = (): void => {
    const trimmed = customInput.trim();
    if (!trimmed) return;
    if (values.includes(trimmed)) {
      setCustomInput('');
      return;
    }
    onChange([...values, trimmed]);
    setCustomInput('');
  };

  const removeCustom = (v: string): void => {
    onChange(values.filter((x) => x !== v));
  };

  return (
    <div className="field">
      <span className="field__label">{label}</span>
      <div className="chips">
        {group.options.map((o) => {
          const selected = values.includes(o.value);
          return (
            <button
              key={o.value}
              type="button"
              className={`chip${selected ? ' chip--selected' : ''}`}
              onClick={() => toggle(o.value)}
            >
              {o.label}
            </button>
          );
        })}
        {customChips.map((v) => (
          <span key={v} className="chip chip--custom">
            {v}
            <button
              type="button"
              className="chip__remove"
              aria-label={`Remove ${v}`}
              onClick={() => removeCustom(v)}
            >
              ×
            </button>
          </span>
        ))}
        {group.allow_other ? (
          <button
            type="button"
            className="chip"
            onClick={() => setCustomOpen((v) => !v)}
          >
            {customOpen ? '− cancel' : '+ Other…'}
          </button>
        ) : null}
      </div>
      {customOpen ? (
        <div className="row" style={{ marginTop: 8 }}>
          <input
            className="input input--small"
            placeholder="Type and press Enter"
            value={customInput}
            maxLength={200}
            onChange={(e) => setCustomInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addCustom();
              }
            }}
          />
          <button type="button" className="btn btn--sm" onClick={addCustom}>
            Add
          </button>
        </div>
      ) : null}
      {hint ? <span className="field__hint">{hint}</span> : null}
    </div>
  );
}
